import {
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requestNoContent,
  requireArray,
  requireBoolean,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import { agentsGridContract, createDataGridClient } from '@/modules/data-grid'

type Schemas = components['schemas']

export type EAutomateConnection = Schemas['EAutomateConnection']
export type EAutomateConnectionInput = Schemas['SaveConnectionRequest']
export type EAutomateAgent = {
  agentId: string
  name: string
  ipAddress: string | null
  osVersion: string | null
  online: boolean
}

function decodeConnection(raw: unknown): EAutomateConnection {
  if (!isRecord(raw)) throw new Error('Malformed e-automate connection')
  return {
    connectionId: requireString(raw.connectionId, 'connectionId'),
    name: requireString(raw.name, 'name'),
    serverAddress: requireString(raw.serverAddress, 'serverAddress'),
    instanceName: nullableString(raw.instanceName, 'instanceName'),
    port: nullableNumber(raw.port, 'port'),
    databaseName: requireString(raw.databaseName, 'databaseName'),
    login: requireString(raw.login, 'login'),
    encrypt: requireBoolean(raw.encrypt, 'encrypt'),
    certificateServerName: nullableString(raw.certificateServerName, 'certificateServerName'),
    agentIds: requireArray(raw.agentIds, 'agentIds', (id) => requireString(id, 'agentId')),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
  } satisfies EAutomateConnection
}

const connectionsPath = (entityId: string) =>
  `/api/v1/entities/${entityId}/eautomate/connections`

const connectionPath = (entityId: string, connectionId: string) =>
  `${connectionsPath(entityId)}/${connectionId}`

async function listAgents(entityId: string, signal: AbortSignal): Promise<EAutomateAgent[]> {
  const agents = createDataGridClient({
    contract: agentsGridContract,
    endpoint: `/api/v1/entities/${entityId}/agents`,
  })
  const found: EAutomateAgent[] = []
  let after: string | undefined
  do {
    const page = await agents.query({
      query: {
        columns: ['agentId', 'name', 'ipAddress', 'osVersion', 'status'],
        filter: [{ column: 'status', operator: 'in', values: ['Online', 'Offline'] }],
        sort: [{ column: 'name', direction: 'asc' }],
        pagination: { limit: 200, after },
      },
      parameters: { includeDescendants: false },
      signal,
    })
    for (const item of page.items)
      found.push({
        agentId: item.key,
        name: item.values.name ?? item.key,
        ipAddress: item.values.ipAddress ?? null,
        osVersion: item.values.osVersion ?? null,
        online: item.values.status === 'Online',
      })
    after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor ?? undefined : undefined
  } while (after)
  return found
}

export const eautomateApi = {
  listConnections: (entityId: string) =>
    request(connectionsPath(entityId), (raw) =>
      requireArray(raw, 'connections', decodeConnection),
    ),
  createConnection: (entityId: string, input: EAutomateConnectionInput) =>
    request(connectionsPath(entityId), decodeConnection, json('POST', input)),
  updateConnection: (
    entityId: string,
    connectionId: string,
    input: EAutomateConnectionInput,
  ) => request(connectionPath(entityId, connectionId), decodeConnection, json('PUT', input)),
  deleteConnection: (entityId: string, connectionId: string) =>
    requestNoContent(connectionPath(entityId, connectionId), { method: 'DELETE' }),
  testConnection: (entityId: string, connectionId: string) =>
    requestNoContent(`${connectionPath(entityId, connectionId)}:test`, { method: 'POST' }),
  listAgents,
}
